import { Fragment, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Menu, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AppLogo } from '@/components/common/AppLogo';
import { AppWordmark } from '@/components/common/AppWordmark';
import { VendorCredit } from '@/components/common/VendorCredit';
import { NAV_SECTIONS, type NavGroup } from '@/config/navigation';
import { SidebarSearch } from './SidebarSearch';
import { UpcomingBadge } from './UpcomingBadge';
import { filterNavSections, useNavGroupActive, useNavVisibilityContext } from './utils/nav.utils';
import {
  CHEVRON_TRANSITION_CLASS,
  CHEVRON_OPEN_CLASS,
  COLLAPSIBLE_CONTENT_CLASS,
  DISABLED_BUTTON_CLASS,
  FOOTER_SEPARATOR_CLASS,
  MENU_LABEL_CLASS,
  SECTION_LABEL_CLASS,
  SECTION_SEPARATOR_CLASS,
  SIDEBAR_BUTTON_CLASS,
  SIDEBAR_SUB_BUTTON_CLASS,
  SUBMENU_ACTIVE_INDICATOR_CLASS,
  SUBMENU_CONTAINER_CLASS,
} from './sidebar.constants';

const SUB_ITEM_BASE_CLASS = 'flex min-w-0 items-center gap-2 text-sm';

const toDataActive = (isActive: boolean): '' | undefined => (isActive ? '' : undefined);

interface MobileNavGroupProps {
  group: NavGroup;
  isForcedOpen: boolean;
  onClose: () => void;
}

function MobileNavGroup({ group, isForcedOpen, onClose }: MobileNavGroupProps) {
  const { isChildActive, isActive } = useNavGroupActive(group);
  const [isManuallyOpen, setIsManuallyOpen] = useState(isActive);
  const isOpen = isForcedOpen || isManuallyOpen;
  const Icon = group.icon;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsManuallyOpen}>
      <CollapsibleTrigger
        data-active={toDataActive(isActive)}
        className={cn('flex w-full items-center text-left', SIDEBAR_BUTTON_CLASS)}
      >
        <Icon />
        <span className={MENU_LABEL_CLASS}>{group.label}</span>
        <ChevronDown
          className={cn(
            'ml-auto size-4 shrink-0 text-muted-foreground/60',
            CHEVRON_TRANSITION_CLASS,
            isOpen && CHEVRON_OPEN_CLASS,
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className={COLLAPSIBLE_CONTENT_CLASS}>
        <div className={SUBMENU_CONTAINER_CLASS}>
          {group.children.map((child) => {
            if (child.upcoming) {
              return (
                <div
                  key={child.href}
                  aria-disabled="true"
                  className={cn(
                    SUB_ITEM_BASE_CLASS,
                    SIDEBAR_SUB_BUTTON_CLASS,
                    DISABLED_BUTTON_CLASS,
                  )}
                >
                  <span className="flex-1 truncate">{child.label}</span>
                  <UpcomingBadge />
                </div>
              );
            }

            const isCurrentPage = isChildActive(child);
            return (
              <Link
                key={child.href}
                to={child.href as '/'}
                onClick={onClose}
                data-active={toDataActive(isCurrentPage)}
                aria-current={isCurrentPage ? 'page' : undefined}
                className={cn(
                  SUB_ITEM_BASE_CLASS,
                  SIDEBAR_SUB_BUTTON_CLASS,
                  isCurrentPage && SUBMENU_ACTIVE_INDICATOR_CLASS,
                )}
              >
                <span className="flex-1 truncate">{child.label}</span>
              </Link>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const navVisibility = useNavVisibilityContext();
  const sections = filterNavSections(NAV_SECTIONS, searchText, navVisibility);
  const isSearching = searchText.trim() !== '';

  function handleOpenChange(shouldOpen: boolean) {
    setIsOpen(shouldOpen);
    if (!shouldOpen) setSearchText('');
  }

  function handleClose() {
    handleOpenChange(false);
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Open menu"
        className="-ml-2 size-9 md:hidden"
        onClick={() => setIsOpen(true)}
      >
        <Menu className="size-5" />
      </Button>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="left"
          className="sidebar-scheme sidebar-surface flex w-72 flex-col gap-0 p-0 text-sidebar-foreground"
        >
          <SheetHeader className="border-b px-4 py-3">
            <SheetTitle>
              <Link to="/" onClick={handleClose} className="flex items-center gap-1">
                <AppLogo />
                <AppWordmark />
              </Link>
            </SheetTitle>
          </SheetHeader>
          <nav className="flex-1 space-y-3 overflow-y-auto px-4 pt-3 pb-4">
            <SidebarSearch value={searchText} onChange={setSearchText} inputClassName="w-full" />

            {sections.length === 0 && (
              <p className="px-2.5 py-6 text-center text-sm text-muted-foreground">
                No menu matches &ldquo;{searchText}&rdquo;
              </p>
            )}

            {sections.map((section, index) => (
              <Fragment key={section.id}>
                {index > 0 && <div className={SECTION_SEPARATOR_CLASS} aria-hidden />}
                <div className="flex flex-col gap-1">
                  {section.label === undefined ? null : (
                    <p className={SECTION_LABEL_CLASS}>{section.label}</p>
                  )}
                  {section.groups.map((group) => (
                    <MobileNavGroup
                      key={group.label}
                      group={group}
                      isForcedOpen={isSearching}
                      onClose={handleClose}
                    />
                  ))}
                </div>
              </Fragment>
            ))}
          </nav>
          <div className={FOOTER_SEPARATOR_CLASS} aria-hidden />
          <div className="px-6 py-4">
            <VendorCredit layout="stacked" />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
